/**
 * PaymentsPage
 *
 * Practice-level payments page showing Stripe Connect status and invoices.
 * Accessible at /practice/payments for billing administrators.
 *
 * Shows cold start experience when Stripe Connect is not set up.
 */

import { Container, Typography, Box, Card, Divider, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useStripeConnect } from '../store/hooks';
import { PaymentsColdStart } from './PaymentsColdStart';
import { StripeConnectStatus } from './StripeConnectStatus';
import { StripeInvoicesTableCard } from './StripeInvoicesTableCard';

export function PaymentsPage() {
  const theme = useTheme();
  const { hasStripeAccount, isLoading } = useStripeConnect();

  // Show loading spinner while checking Stripe status
  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show cold start experience when Stripe is not connected
  if (!hasStripeAccount) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <PaymentsColdStart variant="page" />
      </Container>
    );
  }

  // Connected experience - show status and invoices
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          Patient Payments
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
          Manage your Stripe connection and view patient payment invoices
        </Typography>
      </Box>

      {/* Stripe Connect Status */}
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Connection
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <StripeConnectStatus />
      </Card>

      {/* Invoices Table */}
      <StripeInvoicesTableCard />
    </Container>
  );
}

export default PaymentsPage;
